import { Link } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { Card } from '@/components/ui';
import { Button } from '@/components/ui/button';

interface DesktopRequiredProps {
  /** Name of the feature being blocked, e.g. "Flow Editor". */
  feature: string;
  /** Optional override for the explanation copy. */
  description?: string;
  /** Optional CTA target. Defaults to '/'. */
  backTo?: string;
  /** Optional CTA label. Defaults to 'Back to Dashboard'. */
  backLabel?: string;
}

/**
 * Friendly block surface for features that don't translate to small screens.
 * Render this in place of the feature when `useIsMobile()` returns true so the
 * surrounding AppShell (sidebar / topbar / bottom nav) stays available.
 */
export function DesktopRequired({
  feature,
  description,
  backTo = '/',
  backLabel = 'Back to Dashboard',
}: DesktopRequiredProps) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="max-w-md p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent-blue/10">
          <Monitor className="h-7 w-7 text-accent-blue" />
        </div>
        <h2 className="mb-2 text-xl font-semibold text-foreground">
          {feature} needs a larger screen
        </h2>
        <p className="mb-6 text-sm text-muted-foreground">
          {description ??
            `${feature} is built for fine-grained editing on a wider canvas. Open this view on a laptop or desktop for the full experience.`}
        </p>
        <Button asChild>
          <Link to={backTo}>{backLabel}</Link>
        </Button>
      </Card>
    </div>
  );
}
